import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, UserX, Copy } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

function Closure({ c, onPaid }) {
    const [utr, setUtr] = useState("");
    const [busy, setBusy] = useState(false);
    const pay = async () => {
        setBusy(true);
        try { await api.post(`/admin/account-closures/${c.id}/pay`, { utr }); toast.success(`${c.ref} marked paid`); onPaid(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const overdue = c.status === "PENDING" && c.due_by < new Date().toISOString().slice(0, 10);
    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`closure-${c.ref}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div><p className="font-heading font-extrabold text-navy">{c.ref} · {c.name}</p><p className="text-xs text-navy/50">{c.customer_id} · {c.phone || "no phone"} · requested {fmt(c.created_at)}</p></div>
                <div className="text-right">
                    <p className="font-heading text-xl font-extrabold text-navy" data-testid={`closure-total-${c.ref}`}>{rupee(c.total)}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.status === "PAID" ? "bg-emerald-50 text-emerald-700" : overdue ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{c.status === "PAID" ? `Paid · UTR ${c.utr}` : `${overdue ? "Overdue" : "Due"} by ${fmt(c.due_by)}`}</span>
                </div>
            </div>
            <p className="mt-2 text-xs text-navy/60">Care Wallet {rupee(c.wallet_amount)} + rewards {rupee(c.reward_amount)}</p>
            <div className="mt-3 grid gap-2 rounded-xl bg-navy/[0.04] p-3 text-sm sm:grid-cols-3" data-testid={`closure-bank-${c.ref}`}>
                {[["Account name", c.bank?.account_name], ["Account number", c.bank?.account_number], ["IFSC", c.bank?.ifsc]].map(([l, v]) => (
                    <div key={l}><p className="text-[11px] font-semibold uppercase text-navy/45">{l}</p>
                        <p className="flex items-center gap-1.5 font-mono font-bold text-navy">{v || "—"}{v && c.status === "PENDING" && <button aria-label={`Copy ${l}`} onClick={() => copy(v)}><Copy size={12} className="text-navy/40" /></button>}</p></div>
                ))}
            </div>
            {c.status === "PENDING" && (
                <div className="mt-3 flex flex-wrap gap-2">
                    <input data-testid={`closure-utr-${c.ref}`} value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="Bank UTR / reference" className="flex-1 rounded-full border border-navy/15 px-4 py-2 text-sm outline-none focus:border-orange" />
                    <button data-testid={`closure-pay-${c.ref}`} disabled={busy} onClick={pay} className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-bold text-white disabled:opacity-50">Pay {rupee(c.total)} · mark paid</button>
                </div>
            )}
        </div>
    );
}

export default function AdminAccountClosures() {
    const [rows, setRows] = useState(null);
    const load = () => api.get("/admin/account-closures").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const pending = (rows || []).filter((r) => r.status === "PENDING");
    return (
        <div>
            <div className="flex items-center gap-2"><UserX size={22} className="text-orange" /><h1 className="font-heading text-2xl font-extrabold text-navy">Account Closures</h1></div>
            <p className="text-sm text-navy/55" data-testid="closures-summary">{pending.length} refund(s) to pay · {rupee(pending.reduce((a, r) => a + r.total, 0))} due. Customers who deleted their account asked for their wallet balance and rewards to be credited to this bank account.</p>
            {rows === null && <Loader2 className="mx-auto mt-10 h-7 w-7 animate-spin text-orange" />}
            {rows?.length === 0 && <p className="mt-8 text-center text-sm text-navy/40">No account closure requests.</p>}
            <div className="mt-4 space-y-3">{(rows || []).map((c) => <Closure key={c.id} c={c} onPaid={load} />)}</div>
        </div>
    );
}
